import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

interface CategorySectionProps {
  query: string;
}

export default function CategorySection({ query }: CategorySectionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => t(c.key).toLowerCase().includes(q));
  }, [query, t]);

  return (
    <section id="categories" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-14 md:px-6 md:py-20">
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <div className="max-w-xl">
          <span className="inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
            {t("categories.badge")}
          </span>
          <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t("categories.title")}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            {t("categories.subtitle")}
          </p>
        </div>

        {/* 分类下拉框 */}
        <div className="relative w-full md:w-72" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center gap-3 rounded-lg border border-background-300/70 bg-background-50 px-4 py-3 text-left text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center text-primary-600">
              <i className="ri-grid-line text-lg"></i>
            </span>
            <span className="flex-1 whitespace-nowrap">{t("categories.dropdownLabel")}</span>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={open ? "ri-arrow-up-s-line text-foreground-500" : "ri-arrow-down-s-line text-foreground-500"}></i>
            </span>
          </button>

          {open && (
            <div className="absolute left-0 right-0 z-30 mt-2 max-h-80 overflow-auto rounded-lg border border-background-200 bg-background-50 p-1">
              <p className="px-3 py-2 text-xs text-foreground-500">{t("categories.dropdownPlaceholder")}</p>
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-900"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                  <span className="whitespace-nowrap">{t(c.key)}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 分类按钮网格 */}
      {filtered.length === 0 ? (
        <p className="mt-12 rounded-lg border border-background-200 bg-background-50 p-8 text-center text-sm text-foreground-500">
          {t("categories.empty")}
        </p>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {filtered.map((c) => (
            <a
              key={c.key}
              href={c.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-transform hover:-translate-y-1"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={c.image}
                  alt={t(c.key)}
                  title={`${t(c.key)} — USFans Spreadsheet`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between gap-2 px-4 py-3">
                <span className="truncate text-sm font-semibold text-foreground-900">{t(c.key)}</span>
                <span className="flex w-7 h-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}